import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useGoBack } from '../../hooks/useGoBack'
import { QRCodeSVG } from 'qrcode.react'
import { CheckCircle2, Ticket } from 'lucide-react'
import Header from '../../components/Header'
import PrizeStockBadge from '../../components/PrizeStockBadge'
import StampProgress from '../../components/StampProgress'
import CollectedCharacters from '../../components/CollectedCharacters'
import { showToast } from '../../components/Toast'
import { useI18n } from '../../i18n/useI18n'
import { api, type StampPrizeSummary, type StampProgress as StampProgressData } from '../../services/api'
import { findActivityProgram, findQrProgram, useStampRally } from '../../store/useStampRally'

/**
 * 特典チケット画面 (#1126)。発行条件 = distinct スタンプ >= goal かつ在庫あり。
 * 発行・状態遷移は全てサーバ判定（ボタン活性はUX目的の先回りでしかない）。
 * チケットQR = コードそのまま。運営が /admin/stamp/redeem で読み取り消込する。
 *
 * #1154: ?eventId=<企画id> で AR宝探しラリーのチケットも扱う
 * (未指定 = 最初の QR 企画)。進捗・景品・チケットは企画ごとに独立。
 * eventId 指定時は context 不在 (ArGate/ArReturn からのフルロード直後) でも
 * /stamp/progress で自足するスタンドアロンモードを持つ — TOP へ戻すと
 * ticket ↔ TOP のリダイレクトループになるため (E2E で検出)。
 *
 * #1898 context はイベントごとにまとまっているので、企画は ID でイベントをまたいで引く。
 */
export default function StampTicket() {
  const navigate = useNavigate()

  // #1361 戻るは履歴を1つ戻す（行き先を push すると履歴が積み上がる）
  const goBack = useGoBack('/stamp-rally')
  const [searchParams] = useSearchParams()
  const { t, locale } = useI18n()
  const {
    context, ticket, ticketLoading,
    loadContext, loadTicket, refreshProgress, refreshActivityProgress, claimTicket,
  } = useStampRally()

  const eventIdParam = searchParams.get('eventId')
  const activityBlock = eventIdParam ? findActivityProgram(context, eventIdParam) : null
  const qrBlock = activityBlock ? null : findQrProgram(context, eventIdParam)
  const block = activityBlock ?? qrBlock
  const usingContext = context?.available === true && !!block
  const isActivity = !!activityBlock

  // eventId 直接流入 (context なし) 用の自足データ
  const [standalone, setStandalone] = useState<{
    progress: StampProgressData
    prize: StampPrizeSummary | null
  } | null>(null)

  const event = usingContext ? block!.event : null
  const progress = standalone ? standalone.progress : block?.progress
  const prize = standalone ? standalone.prize : block?.prize
  const targetEventId = eventIdParam ?? event?.id ?? null
  const goal = event?.stampGoal ?? progress?.goal ?? 0

  useEffect(() => {
    if (usingContext) {
      if (isActivity) void refreshActivityProgress()
      else void refreshProgress()
      void loadTicket(targetEventId ?? undefined)
      return
    }
    if (eventIdParam) {
      // スタンドアロンモード: 認証だけあれば表示できる (発行判定は常にサーバ)
      api
        .getStampProgress(eventIdParam)
        .then(({ progress, prize }) => setStandalone({ progress, prize }))
        .catch(() => navigate('/stamp-rally', { replace: true }))
      void loadTicket(eventIdParam)
      return
    }
    // eventId なしの直リンク: 表示条件判定を持つ TOP へ（判定の二重実装を避ける）
    navigate('/stamp-rally', { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!standalone && !(usingContext && event)) {
    return (
      <div className="min-h-screen bg-warm-white">
        <Header title={t('stampRally.ticketTitle')} onBack={goBack} />
        <div className="flex justify-center py-20">
          <div className="w-7 h-7 border-2 border-navy border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    )
  }

  const achieved = progress?.achieved ?? false
  const soldOut = prize?.status === 'sold_out'
  const canClaim = achieved && !soldOut && !ticket && !ticketLoading

  const handleClaim = async () => {
    if (!targetEventId) return
    const result = await claimTicket(targetEventId)
    if (result.ok) {
      showToast({ type: 'success', message: t('stampRally.claimed') })
    } else if (result.reason === 'sold_out') {
      // ボタン表示後に在庫が尽きた競合ケース — サーバ判定が正
      showToast({ type: 'error', message: t('stampRally.stockSoldOut') })
      void loadContext()
    } else if (result.reason === 'not_eligible') {
      showToast({
        type: 'error',
        message: t('stampRally.notEligible', { goal: String(goal) }),
      })
    } else {
      showToast({ type: 'error', message: t('stampRally.scanError') })
    }
  }

  return (
    <div className="min-h-screen bg-warm-white pb-10">
      <Header title={t('stampRally.ticketTitle')} onBack={goBack} />

      <div className="flex flex-col gap-4 px-4 mt-4">
        {/* ---- 発行済みチケット ---- */}
        {ticket ? (
          <div className="bg-white rounded-2xl shadow-card p-6 flex flex-col items-center animate-fade-in">
            {ticket.status === 'redeemed' ? (
              <div className="flex flex-col items-center mb-4">
                <CheckCircle2 size={48} className="text-matcha mb-2" />
                <span className="font-display text-lg text-navy">{t('stampRally.redeemedLabel')}</span>
                {ticket.redeemedAt && (
                  <span className="text-xs font-body text-text-tertiary mt-1">
                    {t('stampRally.redeemedAt')}:{' '}
                    {new Date(ticket.redeemedAt).toLocaleString(locale)}
                  </span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 mb-4">
                <Ticket size={20} className="text-gold" />
                <span className="font-display text-lg text-navy">{prize?.name}</span>
              </div>
            )}

            <div className={ticket.status === 'redeemed' ? 'opacity-30' : ''}>
              <QRCodeSVG value={ticket.code} size={180} level="M" />
            </div>

            <p className="mt-4 text-xs font-body text-text-tertiary">{t('stampRally.ticketCode')}</p>
            <p className="font-mono text-xl tracking-[0.2em] text-text-primary">{ticket.code}</p>

            {ticket.status === 'issued' && (
              <p className="mt-4 text-center text-xs font-body text-text-secondary">
                {t('stampRally.showStaff')}
              </p>
            )}
            <p className="mt-2 text-center text-[11px] font-body text-text-tertiary">
              {t('stampRally.ticketNote')}
            </p>
          </div>
        ) : (
          <>
            {/* ---- 未発行: 進捗 + 発行ボタン ---- */}
            <StampProgress
              count={progress?.stampCount ?? 0}
              goal={goal}
            />
            {prize && <PrizeStockBadge prize={prize} />}
            {/* 達成した瞬間が一番スクショされるので、何を集めたかをここに写す */}
            {targetEventId && <CollectedCharacters eventId={targetEventId} />}

            {achieved && soldOut && (
              <p className="text-center text-sm font-body text-text-secondary px-4">
                {t('stampRally.achievedSoldOut')}
              </p>
            )}
            {!achieved && (
              <p className="text-center text-sm font-body text-text-secondary px-4">
                {t('stampRally.notEligible', { goal: String(goal) })}
              </p>
            )}

            <button
              onClick={() => void handleClaim()}
              disabled={!canClaim}
              className="flex items-center justify-center gap-2 rounded-full bg-gold text-white py-3.5 text-sm font-body font-medium tap-effect disabled:opacity-40"
            >
              <Ticket size={18} />
              {ticketLoading ? t('stampRally.claiming') : t('stampRally.claimTicket')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
